"use client";

import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { IChatResponse, IDeleteChatBulkRequest } from "@finbackoffice/clientbff-client";
import {
    ChatContext,
    ClientBFFContext,
    DEFAULT_PAGINATION,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Loading from "components/base/loading/Loading";
import Translate from "components/base/translate/Translate";
import Pagination from "components/base/pagination/Pagination";
import { RouterQuery } from "utils/constants";
import AccountHeader from "../header/AccountHeader";
import MessagingTools from "./tools/MessagingTools";
import Conversation from "./conversation/Conversation";
import styles from "./account-messages.module.sass";

type IFilter = {
    inbox?: boolean;
    is_starred?: boolean;
    search?: string;
};

function getFilterQuery(directory: string, query?: string): IFilter {
    let filterQuery: IFilter = {};

    if (directory === "inbox") {
        filterQuery = {
            inbox: true,
        };
    }
    if (directory === "sent") {
        filterQuery = {
            inbox: false,
        };
    }

    if (directory === "starred") {
        filterQuery = {
            is_starred: true,
        };
    }

    if (directory === "search" && query) {
        filterQuery = {
            search: query,
        };
    }

    return filterQuery;
}

type IChatsState = {
    chats: IChatResponse[];
    loading: boolean;
};

type IProps = {
    isPageMode?: boolean;
    msgType: RouterQuery;
};

const Messages: FC<IProps> = ({ msgType, isPageMode = false }) => {
    const client = useContext(ClientBFFContext);
    const { unreadMessages } = useContext(ChatContext);
    const { query: queryParams } = useSearchParamsQuery();
    const { search, query } = queryParams;
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [messagingToolsStarValue, setMessagingToolsStarValue] = useState(false);
    const [chatsState, setChatsState] = useState<IChatsState>({
        chats: [],
        loading: true,
    });
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const scope: RouterQuery = search ? RouterQuery.Search : msgType;

    const loadChats = useCallback(async () => {
        if (scope) {
            setChatsState((state) => ({
                ...state,
                loading: true,
            }));
            try {
                const response = await client.queryChat({
                    page: pagination.page,
                    items_per_page: pagination.items_per_page,
                    ...getFilterQuery(scope as string, (query || search) as string),
                });

                setChatsState((state) => ({
                    ...state,
                    chats: response.items,
                    loading: false,
                }));
                setPagination((state) => ({
                    ...state,
                    total_pages: response.total_pages,
                }));
            } catch (err) {
                const error = await (err as any).response?.data;
                console.error(error);
                setChatsState((state) => ({
                    ...state,
                    loading: false,
                }));
            }
        }
    }, [scope, client, pagination.page, pagination.items_per_page, query, search]);

    useEffect(() => {
        void loadChats();
    }, [loadChats]);

    useEffect(() => {
        if (unreadMessages.length) {
            unreadMessages.forEach((message) => {
                const chatIndex = chatsState.chats.findIndex((chat) => chat.id === message.chat.id);
                if (chatIndex !== -1) {
                    const nextChats = chatsState.chats.map((chat, index) => {
                        if (index === chatIndex) {
                            chat.unread_message_count += 1;
                        }
                        return chat;
                    });
                    setChatsState((state) => ({
                        ...state,
                        chats: nextChats,
                    }));
                } else {
                    setChatsState((state) => ({
                        ...state,
                        chats: [message.chat, ...state.chats],
                    }));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unreadMessages]);

    useEffect(() => {
        let starredCount = 0;
        let unstarredCount = 0;

        for (let i = 0; i < selectedIds.length; ++i) {
            const conversation = chatsState.chats.find((item) => item.id === selectedIds[i]);

            if (conversation) {
                if (conversation.is_starred) {
                    starredCount++;
                } else {
                    unstarredCount++;
                }
            }
        }

        setMessagingToolsStarValue(starredCount >= unstarredCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    const onPageChangeHandler = (page: number) => {
        setPagination((state) => ({
            ...state,
            page,
        }));

        setSelectedIds([]);
    };

    const onPerPageHandler = (val: string) => {
        setPagination((state) => ({
            ...state,
            items_per_page: parseInt(val),
            page: 1,
        }));

        setSelectedIds([]);
    };

    const triggerMessageSelectedHandler = (id: number): void => {
        const index: number = selectedIds.indexOf(id);
        if (index === -1) {
            setSelectedIds([...selectedIds, id]);
        } else {
            selectedIds.splice(index, 1);
            setSelectedIds([...selectedIds]);
        }
    };

    const triggerSelectAllHandler = (val: boolean) => {
        if (val) {
            const result: any = [];
            chatsState.chats.forEach((conversation) => result.push(conversation.id));
            setSelectedIds(result);
        } else {
            setSelectedIds([]);
        }
    };

    const updateStarrById = useCallback(
        (id: number, val: boolean) => {
            const result = [...chatsState.chats];
            const chatIndex: number = result.findIndex((r) => r.id === id);

            result[chatIndex].is_starred = val;

            setChatsState((state) => ({
                ...state,
                chats: result,
            }));
        },
        [chatsState.chats],
    );

    const updateChats = useCallback(
        (chats: IChatResponse[]) => {
            const result = [...chatsState.chats];

            chats.forEach((chat) => {
                const chatIndex: number = result.findIndex((r) => r.id === chat.id);
                result[chatIndex] = chat;
            });

            setChatsState((state) => ({
                ...state,
                chats: result,
            }));
        },
        [chatsState.chats],
    );

    const removeChatsById = useCallback(
        (chats: IDeleteChatBulkRequest[]) => {
            const result = [...chatsState.chats];

            for (let i: number = chats.length - 1; i >= 0; --i) {
                const chatIndex: number = result.findIndex((r) => r.id === chats[i].chat_id);
                result.splice(chatIndex, 1);
            }

            setChatsState((state) => ({
                ...state,
                chats: result,
            }));
        },
        [chatsState.chats],
    );

    return (
        <div className={styles.messages} data-testid={`account-messages-${scope}`}>
            <AccountHeader
                headingIcon={
                    !isPageMode
                        ? `/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-${msgType}.svg`
                        : undefined
                }
                tId={
                    !isPageMode
                        ? `account_messages_${
                              scope.includes(RouterQuery.Search) ? scope.split("_")[0] : scope
                          }_heading`
                        : ""
                }>
                {chatsState.chats && (
                    <MessagingTools
                        triggerSelectAll={triggerSelectAllHandler}
                        selectedIds={selectedIds}
                        starValue={messagingToolsStarValue}
                        count={chatsState.chats.length}
                        onStarrClickCallback={updateChats}
                        onRemoveClickCallback={removeChatsById}
                    />
                )}
            </AccountHeader>
            {chatsState.chats && chatsState.chats.length === 0 && (
                <div className={styles.noMessage}>
                    <Translate
                        tid={`account_messages_${
                            scope.includes(RouterQuery.Search) ? scope.split("_")[0] : scope
                        }NoDataMsg`}
                        namespace={TranslationScopes.UserAccount}
                        dangerous>
                        <div />
                    </Translate>
                </div>
            )}
            {chatsState.loading && <Loading testId="loading-spinner" />}
            {chatsState.chats && chatsState.chats.length !== 0 && (
                <>
                    <div className={styles.content}>
                        {chatsState.chats?.map((conversation) => (
                            <Conversation
                                key={conversation.id}
                                conversation={conversation}
                                isPageMode={isPageMode}
                                hasNew={conversation.unread_message_count > 0}
                                checked={selectedIds.indexOf(conversation.id) !== -1}
                                triggerMessageChecked={triggerMessageSelectedHandler}
                                onStarrClickCallback={updateStarrById}
                            />
                        ))}
                    </div>
                    <Pagination
                        items_per_page={pagination.items_per_page}
                        page={pagination.page}
                        total_pages={pagination.total_pages || 0}
                        onPageChange={onPageChangeHandler}
                        onPerPage={onPerPageHandler}
                    />
                </>
            )}
        </div>
    );
};

export default Messages;
